import {
  BoxGeometry,
  BufferGeometry,
  Float32BufferAttribute,
  Group,
  Mesh,
} from 'three';
import type { HouseBodyConfig, RoofConfig, TerraceConfig } from '@/model/schema';
import type { MaterialKit } from '../materials';
import { terraceAabb } from '@/generator/extrasGenerator';

/**
 * Roof builder: flat slab, gable prism, or hip pyramid.
 *
 * All three sit on top of the topmost story and cover the footprint plus
 * overhang. Geometry is parametric; we don't use CSG. The gable and hip
 * variants are authored as custom BufferGeometry so the export is clean.
 */
export function buildRoof(
  body: HouseBodyConfig,
  roof: RoofConfig,
  topElevation: number,
  kit: MaterialKit,
  terraces: TerraceConfig[] = [],
): Group {
  const group = new Group();
  group.name = 'Roof';
  group.position.y = topElevation;

  const w = body.footprintWidth + 2 * roof.overhang;
  const d = body.footprintDepth + 2 * roof.overhang;

  // Top-story terrace notches punch through the roof. When any are
  // present we force a flat roof and decompose it into a grid of
  // slabs, omitting cells inside any notch. Sloped roofs (gable / hip)
  // don't yet support a non-convex footprint; deferring until the
  // top-story terrace variant is stable.
  const topLevel = findTopLevel(terraces);
  const topNotches =
    topLevel !== null
      ? terraces
          .filter((t) => t.level === topLevel)
          .map((t) => notchAabbRoofLocal(body, t, roof.overhang))
      : [];

  if (topNotches.length > 0) {
    // Flat-cut roofs must use a flat-roof thickness (~0.25 m) regardless
    // of the incoming `roof.height`. A gable/hip roof's height is its
    // peak elevation (2-3m); using that as a slab thickness produces a
    // giant black cube over the terrace story. The generator already
    // forces `type = 'flat'` + `height = 0.25` when a terrace is
    // present, but we defensively clamp here too in case a hand-crafted
    // config slips through.
    const flatThickness = roof.type === 'flat' ? roof.height : 0.25;
    buildCutFlatRoof(group, w, d, flatThickness, topNotches, kit);
  } else {
    switch (roof.type) {
      case 'flat': {
        const slab = new BoxGeometry(w, roof.height, d);
        const mesh = new Mesh(slab, kit.roof);
        mesh.name = 'RoofFlat';
        mesh.position.y = roof.height / 2 + 0.001;
        mesh.castShadow = true;
        mesh.receiveShadow = true;
        group.add(mesh);
        break;
      }
      case 'gable': {
        const mesh = buildGable(w, d, roof.height, roof.direction ?? 'x', kit);
        group.add(mesh);
        break;
      }
      case 'hip': {
        const mesh = buildHip(w, d, roof.height, kit);
        group.add(mesh);
        break;
      }
    }
  }

  group.position.x = body.originX + body.footprintWidth / 2;
  group.position.z = body.originZ + body.footprintDepth / 2;

  return group;
}

/** Highest terrace story across the input array (or null if empty). */
function findTopLevel(terraces: TerraceConfig[]): number | null {
  let top: number | null = null;
  for (const t of terraces) {
    if (top === null || t.level > top) top = t.level;
  }
  return top;
}

/**
 * Terrace AABB in ROOF-local coordinates (origin at body center,
 * roof extents ±(w/2, d/2) where w, d include overhang). The notch
 * aligns with the body, NOT with the overhang, so the notch sits
 * inset from the roof edges by `roof.overhang` on the side coplanar
 * with the host facade — except that the host-facade side of the
 * notch is EXTENDED to the roof edge (through the overhang) so the
 * roof opening reaches the eaves.
 */
function notchAabbRoofLocal(
  body: HouseBodyConfig,
  t: TerraceConfig,
  overhang: number,
) {
  const W = body.footprintWidth;
  const D = body.footprintDepth;
  // Body-local AABB from the generator (single source of truth).
  const a = terraceAabb(body, t);
  // Roof-local (origin at body centre): subtract W/2 / D/2.
  let rxLo = a.xLo - W / 2;
  let rxHi = a.xHi - W / 2;
  let rzLo = a.zLo - D / 2;
  let rzHi = a.zHi - D / 2;
  // Corner terraces have TWO open facade edges (host + adjacent) and
  // TWO interior-boundary edges (where the terrace walls stand).
  //   - On the open facade edges, push the roof cut OUT through the
  //     overhang so the eave doesn't jut over the open terrace.
  //   - On the interior-boundary edges, pull the roof cut IN by a
  //     small eave so the roof extends slightly past the terrace
  //     walls, hiding the wall tops from view.
  const TERRACE_EAVE = 0.2;
  if (Math.abs(a.xLo) < 1e-4) rxLo = -W / 2 - overhang;
  else rxLo += TERRACE_EAVE;
  if (Math.abs(a.xHi - W) < 1e-4) rxHi = W / 2 + overhang;
  else rxHi -= TERRACE_EAVE;
  if (Math.abs(a.zLo) < 1e-4) rzLo = -D / 2 - overhang;
  else rzLo += TERRACE_EAVE;
  if (Math.abs(a.zHi - D) < 1e-4) rzHi = D / 2 + overhang;
  else rzHi -= TERRACE_EAVE;
  return { xLo: rxLo, xHi: rxHi, zLo: rzLo, zHi: rzHi };
}

/** Build a flat roof split into a grid of slabs, omitting any grid
 *  cells that fall inside any notch. Same decomposition trick as the
 *  multi-hole floor-slab cutter in buildStory. */
function buildCutFlatRoof(
  group: Group,
  w: number,
  d: number,
  height: number,
  notches: Array<{ xLo: number; xHi: number; zLo: number; zHi: number }>,
  kit: MaterialKit,
): void {
  const xMin = -w / 2;
  const xMax = w / 2;
  const zMin = -d / 2;
  const zMax = d / 2;
  const xEdges = uniqueSorted([
    xMin,
    xMax,
    ...notches.flatMap((n) => [clamp(n.xLo, xMin, xMax), clamp(n.xHi, xMin, xMax)]),
  ]);
  const zEdges = uniqueSorted([
    zMin,
    zMax,
    ...notches.flatMap((n) => [clamp(n.zLo, zMin, zMax), clamp(n.zHi, zMin, zMax)]),
  ]);
  let cellIndex = 0;
  for (let i = 0; i < xEdges.length - 1; i++) {
    for (let j = 0; j < zEdges.length - 1; j++) {
      const x0 = xEdges[i]!;
      const x1 = xEdges[i + 1]!;
      const z0 = zEdges[j]!;
      const z1 = zEdges[j + 1]!;
      const sx = x1 - x0;
      const sz = z1 - z0;
      if (sx < 0.01 || sz < 0.01) continue;
      const cx = (x0 + x1) / 2;
      const cz = (z0 + z1) / 2;
      const inHole = notches.some(
        (n) => cx > n.xLo && cx < n.xHi && cz > n.zLo && cz < n.zHi,
      );
      if (inHole) continue;
      const mesh = new Mesh(new BoxGeometry(sx, height, sz), kit.roof);
      mesh.name = `RoofFlat_${cellIndex.toString().padStart(3, '0')}`;
      mesh.position.set(cx, height / 2 + 0.001, cz);
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      group.add(mesh);
      cellIndex++;
    }
  }
}

function uniqueSorted(values: number[]): number[] {
  const sorted = values.slice().sort((a, b) => a - b);
  const out: number[] = [];
  for (const v of sorted) {
    if (out.length === 0 || v - out[out.length - 1]! > 1e-4) out.push(v);
  }
  return out;
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}

function buildGable(
  width: number,
  depth: number,
  height: number,
  ridgeAxis: 'x' | 'z',
  kit: MaterialKit,
): Mesh {
  // Prism with ridge along `ridgeAxis`. 6 vertices, 8 triangles (2 slopes,
  // 2 gable ends).
  const hw = width / 2;
  const hd = depth / 2;

  let verts: number[];
  let tris: number[];

  // Winding convention: all triangles listed so that (b-a)×(c-a) points
  // OUTWARD from the prism. Tripped us up before — FrontSide culling
  // would hide slopes that were wound inward. Verified face-by-face in
  // the tests near this module.
  if (ridgeAxis === 'x') {
    // Ridge runs along X axis at the top (y=height), at z=0.
    verts = [
      -hw, 0, -hd, // 0 front-left eaves
      hw, 0, -hd,  // 1 front-right eaves
      hw, 0, hd,   // 2 back-right eaves
      -hw, 0, hd,  // 3 back-left eaves
      -hw, height, 0, // 4 ridge-left
      hw, height, 0,  // 5 ridge-right
    ];
    // Outward-normal audit (hw=hd=height=1 for signs):
    //   front slope (should be +Y/-Z):
    //     (0,4,5): cross((0,1,1),(2,1,1)) = (0,+2,-2) ✓
    //     (0,5,1): cross((2,1,1),(2,0,0)) = (0,+2,-2) ✓
    //   back slope (should be +Y/+Z):
    //     (2,4,3): cross((-2,1,-1),(-2,0,0)) = (0,+2,+2) ✓
    //     (2,5,4): cross((0,1,-1),(-2,1,-1)) = (0,+2,+2) ✓
    //   left gable (-X):  (3,4,0) = (-2,0,0) ✓
    //   right gable (+X): (1,5,2) = (+2,0,0) ✓
    tris = [
      // front slope (outward normal +Y/-Z)
      0, 4, 5, 0, 5, 1,
      // back slope (outward normal +Y/+Z)
      2, 4, 3, 2, 5, 4,
      // left gable (outward normal -X)
      3, 4, 0,
      // right gable (outward normal +X)
      1, 5, 2,
    ];
  } else {
    // Ridge runs along Z axis at the top, at x=0.
    verts = [
      -hw, 0, -hd,
      hw, 0, -hd,
      hw, 0, hd,
      -hw, 0, hd,
      0, height, -hd,
      0, height, hd,
    ];
    tris = [
      // right slope (outward normal +X/+Y) — was wound inside-out
      1, 5, 2, 1, 4, 5,
      // left slope (outward normal -X/+Y) — was wound inside-out
      3, 4, 0, 3, 5, 4,
      // front gable (outward normal -Z)
      0, 4, 1,
      // back gable (outward normal +Z)
      2, 5, 3,
    ];
  }

  const geo = new BufferGeometry();
  geo.setAttribute('position', new Float32BufferAttribute(verts, 3));
  geo.setIndex(tris);
  geo.computeVertexNormals();

  const mesh = new Mesh(geo, kit.roofSlope);
  mesh.name = 'RoofGable';
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  return mesh;
}

function buildHip(width: number, depth: number, height: number, kit: MaterialKit): Mesh {
  // Truncated pyramid: eaves at y=0 form a rectangle, apex at (0, h, 0).
  // We build it as 4 triangular slopes from a single apex.
  const hw = width / 2;
  const hd = depth / 2;
  const verts = [
    -hw, 0, -hd, // 0 front-left
    hw, 0, -hd,  // 1 front-right
    hw, 0, hd,   // 2 back-right
    -hw, 0, hd,  // 3 back-left
    0, height, 0, // 4 apex
  ];
  // Each slope is wound so its outward normal points away from the apex
  // (i.e. front slope's normal has -Z and +Y components). Previously all
  // four triangles were wound inside-out and the roof's entire top disappeared
  // under FrontSide culling.
  const tris = [
    0, 4, 1, // front slope (normal -Z/+Y)
    1, 4, 2, // right slope (normal +X/+Y)
    2, 4, 3, // back slope  (normal +Z/+Y)
    3, 4, 0, // left slope  (normal -X/+Y)
  ];
  const geo = new BufferGeometry();
  geo.setAttribute('position', new Float32BufferAttribute(verts, 3));
  geo.setIndex(tris);
  geo.computeVertexNormals();

  const mesh = new Mesh(geo, kit.roofSlope);
  mesh.name = 'RoofHip';
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  return mesh;
}
